import findReceiptDialogTemplate from './FindReceiptDialog.html';

/*******************************************************************************
 * @ngdoc    service
 * @name     csm.books.WriteReceipts.service:FindReceiptDialog
 * @description
 * This provides a means to display a dialog that contains filter for receipt
 * filtering. The list of found receipts and filters are cached.
 *******************************************************************************/
/* @ngInject */ export function FindReceiptDialogService(
  $uibModal,
  uiGridConstants
) {
  var mSavedFilters = null;
  var mReceivedFrom = null;

  /*---------*/
  /* Service */
  /*---------*/
  var $svc = Object.assign(this, {
    foundReceipts: [],
    /** Pagination info from the last search, kept so reopening the dialog
     *  shows the cached results with a working pager. */
    pagination: null,

    /**
     * @ngdoc       function
     * @name        reset
     * @description Clear the saved filters and removed any found receipts.
     */
    reset: function () {
      mSavedFilters = null;
      mReceivedFrom = null;
      $svc.foundReceipts = [];
      $svc.pagination = null;
    },

    /**
     * @ngdoc       function
     * @name        show
     * @description Show the dialog.
     * @param       {array}  entities  List of entities to search against.
     * @return      {object}           $uibModal.open() result.
     */
    show: function (entities) {
      return $uibModal.open({
        animation: true,
        backdrop: 'static',
        controller: FindReceiptsController,
        size: 'lg',
        template: findReceiptDialogTemplate
      }).result;
    }
  });

  /*------------------------------------------*/
  /* template to add ng-dblClick to each row. */
  /*------------------------------------------*/
  var dblClickTemplate =
    '<div ng-dblclick="grid.appScope.viewReceipt(row.entity)" ng-repeat="(colRenderIndex, col) in colContainer.renderedColumns track by col.colDef.name" class="ui-grid-cell" ui-grid-cell></div>';

  /*****************************************************************************
   * Controller
   *****************************************************************************/
  function FindReceiptsController(
    $scope,
    Alert,
    Tools,
    BooksSvc,
    BusyScreen,
    BooksSettings,
    BarcodeValidator,
    SERVER,
    $q
  ) {
    /*------------------*/
    /* Find Grid Config */
    /*------------------*/
    $scope.findGridOptions = {
      enableColumnMenus: false,
      enableSorting: true,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
      enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      enableRowHeaderSelection: true,
      enableFullRowSelection: true,
      multiSelect: false,
      onRegisterApi: function (gridApi) {
        $scope.findGridOptions.api = gridApi;
      },
      rowTemplate: dblClickTemplate,
      columnDefs: [
        {
          field: 'entryDate',
          minWidth: 100,
          cellFilter: "date: 'MM/dd/yyyy'",
          displayName: 'Date',
          type: 'date'
        },
        { field: 'number', maxWidth: 120, type: 'number' },
        { field: 'receivedFrom', cellTooltip: true, minWidth: 180 },
        {
          field: 'amount',
          maxWidth: 120,
          cellFilter: 'currency',
          type: 'number'
        },
        { field: 'memo', cellTooltip: true, minWidth: 180 },
        {
          field: 'status',
          width: 150,
          cellTooltip: true,
          labelFunction: function (receipt) {
            const mainStatus = receipt.cleared
              ? 'Cleared'
              : receipt.deposited
                ? 'Deposited'
                : receipt.voided
                  ? 'Void'
                  : '';

            const returnStatus = receipt.returned
              ? 'Returned'
              : receipt.partiallyReturned
                ? 'Partially Returned'
                : '';

            return mainStatus && returnStatus
              ? `${mainStatus} & ${returnStatus}`
              : mainStatus || returnStatus || '';
          }
        },
        {
          field: 'type',
          maxWidth: 120,
          labelFunction: function (receipt) {
            return receipt.type === SERVER.BooksDBConstants.LET_RECEIPT
              ? 'Sale'
              : 'Return';
          }
        }
      ],
      data: $svc.foundReceipts
    };

    /*****************************************************************************
     * Getter/Setters
     *****************************************************************************/
    /***************************************************************************
     * endAmt */
    /**
     * getterSetter for the ending receipt amount. Ensures only numeric values
     * are used.
     ***************************************************************************/
    $scope.endAmt = function (value) {
      if (arguments.length) {
        if (!value) $scope.filters.endAmt = NaN;
        else if (!isNaN(value))
          $scope.filters.endAmt = Tools.roundToPrecision(value, 2);
      } else return $scope.filters.endAmt;
    };

    /***************************************************************************
     * receiptNumber */
    /**
     * getterSetter for the receipt number filter. The value will be rounded to
     * the nearest whole number when valid.
     ***************************************************************************/
    $scope.receiptNumber = function (value) {
      if (arguments.length) {
        var bc = BarcodeValidator.parse(value);
        var validReceipt = bc.isValid && (bc.isReceipt || !bc.isPO);

        $scope.filters.receiptNumberInput = validReceipt ? value : null;
        $scope.filters.batchNumber = validReceipt ? bc.batchNumber : null;
        $scope.filters.receiptNumber = validReceipt
          ? bc.receiptNumber || bc.number
          : null;

        if (value) $scope.clearStartDate();
      } else return $scope.filters.receiptNumberInput;
    };

    /***************************************************************************
     * receivedFrom */
    /**
     * GetterSetter for the receivedFrom field. On a set, the value is saved
     * to the stored property and the filters object is updated accordingly.
     *
     * @param value  AddressEntityListItem to set
     ***************************************************************************/
    $scope.receivedFrom = function (value) {
      if (!arguments.length) return mReceivedFrom;

      mReceivedFrom = value;

      if ($scope.filters) {
        const entity = mReceivedFrom && mReceivedFrom.entity;
        const id = entity ? entity.id : NaN;
        $scope.filters.entityID = id;
      }
    };

    /***************************************************************************
     * startAmt */
    /**
     * getterSetter for the starting receipt amount. Called on blue. Ensures
     * only numeric values are used.
     ***************************************************************************/
    $scope.startAmt = function (value) {
      if (arguments.length) {
        if (!value) $scope.filters.startAmt = NaN;
        else if (!isNaN(value))
          $scope.filters.startAmt = Tools.roundToPrecision(value, 2);
      } else return $scope.filters.startAmt;
    };

    /*****************************************************************************
     * Methods
     *****************************************************************************/

    /***************************************************************************
     * clearStartDate */
    /**
     * Reset the start date filter.
     ***************************************************************************/
    $scope.clearStartDate = function clearStartDate() {
      $scope.filters.startDate = null;
    };

    /***************************************************************************
     * clear */
    /**
     * Reset the filters and remove any currently found receipts.
     ***************************************************************************/
    $scope.clear = function () {
      $svc.reset();
      $scope.findGridOptions.api?.selection.clearSelectedRows();
      $scope.findGridOptions.data = $svc.foundReceipts;
      $scope.pagination = null;
      newFilters();
      $scope.clearStartDate();
    };

    /***************************************************************************
     * close */
    /**
     * This is called when the dialog is to be closed. The current filters are
     * saved and the dialog promise is rejected.
     ***************************************************************************/
    $scope.close = function () {
      $scope.$dismiss();
    };

    /** Label function expression for Received From. */
    $scope.entityLabel = BooksSettings.addressEntityLabel;

    /***************************************************************************
     * findEntities */
    /**
     * Used by the lazyTypeahead component to provide the ReceiptFrom filter.
     *
     * @param  input  Input text to search for
     * @return        Promise that resolves with the matching entities
     ***************************************************************************/
    $scope.findEntities = function (input) {
      /*----------------------------------------------------*/
      /* When there is no input text, resolve with nothing, */
      /* this is called when the from model is cleared and */
      /* the lazyTypeahead is attempting to set the model */
      /*----------------------------------------------------*/
      if (!input) return $q.resolve();

      return BooksSvc.findAddressEntities(
        input,
        BooksSettings.ledgerEntityTypes(),
        true
      ).then(function (resp) {
        return resp.data.sort(function (a, b) {
          return a.entity.fullName
            .toLowerCase()
            .localeCompare(b.entity.fullName.toLowerCase());
        });
      });
    };

    /***************************************************************************
     * newFilters */
    /**
     * Reset the receipt filters and clear any saved filters.
     ***************************************************************************/
    function newFilters() {
      $scope.filters = {
        receiptNumber: null,
        handwritten: null,
        entityID: NaN,
        receivedFrom: null,
        startAmt: NaN,
        endAmt: NaN,
        startDate: Tools.lastFiscalYearStart(
          SERVER.BooksOrgInfo.fyMonth,
          SERVER.BooksOrgInfo.fyDay
        ),
        endDate: null
      };

      mSavedFilters = null;
      mReceivedFrom = null;
    }

    /***************************************************************************
     * receiptSelected */
    /**
     * Returns an indication that a receipt has been selected.
     ***************************************************************************/
    $scope.receiptSelected = function () {
      return $scope.findGridOptions.api?.selection.getSelectedCount() > 0;
    };

    /***************************************************************************
     * search */
    /**
     * Attempts to find receipts corresponding to the current filters. Results
     * come back one page at a time; the grid-pagination component below the
     * results grid calls this with the page the user asked for.
     *
     * @param  page  Page number to fetch, defaults to the first page.
     ***************************************************************************/
    $scope.search = function (page) {
      BusyScreen.setBusy(true);

      /**
       * Number of receipts fetched per page. Keeps the search fast since the
       * receipt status has to be computed for every returned receipt.
       * */
      $scope.filters.page = page || 1;
      $scope.filters.pageSize = 100;

      BooksSvc.findReceipts($scope.filters)
        .then(function (resp) {
          $scope.findGridOptions.api?.selection.clearSelectedRows();
          $scope.findGridOptions.data = $svc.foundReceipts = resp.data.receipts;

          // The grid-pagination component reads the paging info
          // (page/totalPages/totalReceiptCount) off of the response.
          $scope.pagination = $svc.pagination = resp.data;

          if (resp.data.totalReceiptCount == 0)
            Alert.info('No receipts were found', 'No Receipts Found');
          else if (resp.data.totalReceiptCount == 1)
            $scope.viewReceipt($svc.foundReceipts[0]);
        })
        .catch(function (error) {
          Alert.error(
            error.parsedResponse?.message() ||
              'Failed to pull up receipts. Please try again.',
            'Find Receipts Error'
          );
        })
        .finally(function () {
          BusyScreen.setBusy(false);
        });
    };

    /***************************************************************************
     * viewReceipt */
    /**
     * View button click handler. Resolve promise with passed receipt object or
     * the selected receipt.
     ***************************************************************************/
    $scope.viewReceipt = function (receipt) {
      if (!receipt)
        receipt = $scope.findGridOptions.api?.selection.getSelectedRows()[0];

      $scope.$close(receipt);
    };

    /*---------------------------------*/
    /* Restore the saved filters if    */
    /* available or set up new filters */
    /*---------------------------------*/
    newFilters();

    /*----------------------------------------------------*/
    /* Restore the pagination info for any cached results */
    /* so the pager still works when the dialog reopens.  */
    /*----------------------------------------------------*/
    $scope.pagination = $svc.pagination;
  }
}
